import React from 'react'
import { Check } from 'react-bootstrap-icons'

/**
 * Relative luminance, so the tick drawn on a swatch stays legible on both a
 * near-black and a near-white choice. The sRGB coefficients are the usual
 * WCAG ones; the 0.6 cut is where a white tick stops reading.
 */
const isLight = (hex) => {
  const raw = String(hex || '').replace(/^#/, '')
  if (raw.length !== 6) return false
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(raw.slice(i, i + 2), 16) / 255)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6
}

/**
 * Colour field: a row of preset swatches, the OS colour dialog, and the hex
 * itself.
 *
 * All three edit one value. The swatches are what an admin actually uses —
 * picking a brand colour off a row is a single click, where a colour dialog is
 * three. The dialog and the hex box are there for the campaign that arrives
 * with its own colour, so the presets never become a ceiling.
 *
 * `onChange` is called with a `#rrggbb` string for the swatches and the dialog.
 * The hex box passes through whatever is typed — a half-finished `#1a7` has to
 * survive being typed — so callers normalise before saving; the API does the
 * same on write.
 *
 * @param {{value?: string, onChange: (hex: string) => void,
 *          presets?: Array<{value: string, label?: string}|string>,
 *          id?: string, disabled?: boolean}} props
 */
const ColorPicker = ({ value = '', onChange, presets = [], id, disabled = false }) => {
  const resolve = (preset) =>
    typeof preset === 'string' ? { value: preset, label: preset } : preset

  // `<input type="color">` refuses anything that isn't exactly `#rrggbb` and
  // silently shows black instead, so give it a valid value while the hex box
  // is mid-edit.
  const current = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'
  const inputId = id || 'app-modal-color'

  return (
    <div className='app-modal__color'>
      <div className='app-modal__color-swatches'>
        {presets.map((preset) => {
          const item = resolve(preset)
          const selected =
            String(item.value).toLowerCase() === String(value).toLowerCase()
          return (
            <button
              key={item.value}
              type='button'
              className={`app-modal__color-dot ${
                selected ? 'app-modal__color-dot--on' : ''
              }`.trim()}
              style={{ backgroundColor: item.value }}
              aria-pressed={selected}
              aria-label={item.label || item.value}
              title={item.label || item.value}
              disabled={disabled}
              onClick={() => onChange(item.value)}
            >
              {selected && (
                <Check
                  className={
                    isLight(item.value) ? 'text-gray-800' : 'text-white'
                  }
                />
              )}
            </button>
          )
        })}
      </div>

      <div className='app-modal__color-custom'>
        <input
          id={inputId}
          type='color'
          className='app-modal__color-input'
          value={current}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label='Pick a custom colour'
        />
        <input
          type='text'
          className='form-control form-control-solid app-modal__color-hex'
          value={value}
          placeholder='#1a7fd4'
          maxLength={7}
          spellCheck={false}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label='Colour hex code'
        />
      </div>
    </div>
  )
}

export default ColorPicker
